/**
 * Phone app lock: after the app has been in the background longer than the chosen time (Settings →
 * Security, 5 minutes by default) it covers everything and asks for Face ID / fingerprint by itself.
 * Only in the iPhone/Android apps and only with Face ID / fingerprint sign-in turned on.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useT } from '../i18n.ts';
import { logout } from '../store.ts';
import { clearHidden, faceIdOn, lockDue, noteHidden, unlockWithBiometrics } from '../faceid.ts';
import { isNative, nativePlatform } from '../native.ts';

export function AppLock() {
  const t = useT();
  // Decided before the first paint, so a cold start never flashes the chats.
  const [locked, setLocked] = useState(() => lockDue());
  const [busy, setBusy] = useState(false);
  const lockedRef = useRef(locked);
  const busyRef = useRef(false);
  lockedRef.current = locked;

  const unlock = useCallback(async () => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    const ok = await unlockWithBiometrics();
    busyRef.current = false;
    setBusy(false);
    if (ok) {
      clearHidden();
      setLocked(false);
    }
  }, []);

  const check = useCallback(async () => {
    if (!lockDue()) return;
    setLocked(true);
    if (!(await faceIdOn())) {
      // No Face ID / fingerprint on this phone (any more): nothing to unlock with, so no lock.
      clearHidden();
      setLocked(false);
      return;
    }
    void unlock();
  }, [unlock]);

  useEffect(() => {
    void check();
    const onVisibility = () => {
      // The Face ID / fingerprint sheet itself can hide the page for a moment: ignore that.
      if (busyRef.current) return;
      if (document.visibilityState === 'hidden') {
        if (!lockedRef.current) noteHidden();
      } else if (lockedRef.current) void unlock();
      else void check();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [check, unlock]);

  if (!locked) return null;
  const android = isNative && nativePlatform === 'android';
  return (
    <div className="app-lock" role="dialog" aria-modal="true" aria-label={t('lockTitle')}>
      <img src="/logo.png" alt="" width="88" height="88" />
      <h2>{t('lockTitle')}</h2>
      <button className="btn primary faceid-btn" onClick={() => void unlock()} disabled={busy}>
        {android ? t('lockUnlockBio') : t('lockUnlockFace')}
      </button>
      <button className="link-btn" onClick={() => void logout()}>{t('lockUsePassword')}</button>
    </div>
  );
}
